<template>
    <div class="authorize-access">
        <v-container class="pa-2 pa-sm-4" fluid>
            <!-- Loading State -->
            <div v-if="loading" class="d-flex flex-column align-center justify-center pa-8">
                <v-progress-circular indeterminate color="primary" size="60" class="mb-4"></v-progress-circular>
                <p class="text-center subtitle-1 grey--text">{{$t('cargando')}}...</p>
            </div>

            <!-- Fatal Error State (no se pudo cargar el acceso) -->
            <div v-else-if="error">
                <v-card class="elevation-2">
                    <v-card-text class="pa-4 pa-sm-6">
                        <div class="state-banner state-banner--error">
                            <v-icon color="error" size="32" class="mr-3">error_outline</v-icon>
                            <div class="state-banner__text">
                                <div class="state-banner__title">{{ error }}</div>
                                <div v-if="errorDetail" class="state-banner__detail">{{ errorDetail }}</div>
                            </div>
                        </div>

                        <v-layout row wrap justify-center class="mt-4">
                            <v-flex xs12 sm6 md4 class="pa-2">
                                <v-btn color="grey" dark large block @click="$router.go(-1)" class="font-weight-bold">
                                    <v-icon left>arrow_back</v-icon>
                                    {{$t('volver')}}
                                </v-btn>
                            </v-flex>
                            <v-flex xs12 sm6 md4 class="pa-2">
                                <v-btn color="primary" outline large block @click="loadAccessDetails" class="font-weight-bold">
                                    <v-icon left>refresh</v-icon>
                                    {{$t('reintentar')}}
                                </v-btn>
                            </v-flex>
                        </v-layout>
                    </v-card-text>
                </v-card>
            </div>

            <!-- Main Content -->
            <div v-else-if="acceso">
                <!-- Toolbar -->
                <v-toolbar flat color="primary" dark class="mb-4 rounded-lg" elevation="2">
                    <v-icon class="mr-3">assignment_turned_in</v-icon>
                    <v-toolbar-title class="headline font-weight-bold">{{$t('autorizar_acceso')}}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-chip color="white" text-color="primary" label class="font-weight-bold hidden-xs-only">
                        <v-icon small left>confirmation_number</v-icon>
                        #{{ acceso.invitacion.id }}
                    </v-chip>
                </v-toolbar>

                <!-- Already processed banner -->
                <div v-if="acceso.invitacion.authorized || acceso.invitacion.declined" class="state-banner mb-4"
                     :class="acceso.invitacion.authorized ? 'state-banner--success' : 'state-banner--error'">
                    <v-icon :color="acceso.invitacion.authorized ? 'success' : 'error'" size="32" class="mr-3">
                        {{ acceso.invitacion.authorized ? 'check_circle' : 'cancel' }}
                    </v-icon>
                    <div class="state-banner__text">
                        <div class="state-banner__title">
                            {{ acceso.invitacion.authorized ? $t('authorized_access') : $t('deny') }}
                        </div>
                        <div class="state-banner__detail">{{$t('no_acciones_disponibles')}}</div>
                    </div>
                </div>

                <!-- Info Grid -->
                <v-layout row wrap class="mb-2">
                    <!-- Guest Information -->
                    <v-flex xs12 sm6 class="pa-2">
                        <v-card elevation="2" class="fill-height">
                            <v-card-title class="primary--text pb-0">
                                <v-icon small left color="primary">person</v-icon>
                                {{$t('Visitante')}}
                            </v-card-title>
                            <v-divider class="mt-2"></v-divider>
                            <v-card-text class="pa-4">
                                <div class="info-field mb-3">
                                    <p class="info-field__label">{{$t('nombre')}}</p>
                                    <p class="info-field__value">{{ acceso.invitado.nombre_completo }}</p>
                                </div>
                                <div class="info-field mb-3">
                                    <p class="info-field__label">Email</p>
                                    <p class="info-field__value info-field__value--break">{{ acceso.invitado.email }}</p>
                                </div>
                                <div class="info-field mb-3">
                                    <p class="info-field__label">{{$t('celular')}}</p>
                                    <p class="info-field__value">{{ acceso.invitado.celular }}</p>
                                </div>
                                <div class="info-field">
                                    <p class="info-field__label">{{$t('Estado')}}</p>
                                    <v-chip :color="invitacionStatusColor" text-color="white" small class="ml-0">
                                        {{ invitacionStatusLabel }}
                                    </v-chip>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-flex>

                    <!-- Invitation / Host Information -->
                    <v-flex xs12 sm6 class="pa-2">
                        <v-card elevation="2" class="fill-height">
                            <v-card-title class="primary--text pb-0">
                                <v-icon small left color="primary">business</v-icon>
                                {{$t('informacion_invitacion')}}
                            </v-card-title>
                            <v-divider class="mt-2"></v-divider>
                            <v-card-text class="pa-4">
                                <div class="info-field mb-3">
                                    <p class="info-field__label">{{$t('Empresa')}}</p>
                                    <p class="info-field__value">{{ acceso.empresa.nombre }}</p>
                                </div>
                                <div class="info-field mb-3">
                                    <p class="info-field__label">{{$t('Anfitrion')}}</p>
                                    <p class="info-field__value">{{ acceso.anfitrion.nombre_completo }}</p>
                                </div>
                                <div class="info-field mb-3">
                                    <p class="info-field__label">Email {{$t('Anfitrion')}}</p>
                                    <p class="info-field__value info-field__value--break">{{ acceso.anfitrion.email }}</p>
                                </div>
                                <div class="d-flex flex-wrap align-center" style="gap: 24px;">
                                    <div class="info-field">
                                        <p class="info-field__label">{{$t('confirmada')}}</p>
                                        <v-chip :color="acceso.invitacion.confirmed ? 'success' : 'warning'" text-color="white" small class="ml-0">
                                            {{ acceso.invitacion.confirmed ? $t('Si') : $t('pendiente') }}
                                        </v-chip>
                                    </div>
                                    <div class="info-field" v-if="acceso.invitacion.count !== undefined">
                                        <p class="info-field__label">{{$t('veces_utilizada')}}</p>
                                        <p class="info-field__value">{{ acceso.invitacion.count }}</p>
                                    </div>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-flex>
                </v-layout>

                <!-- No Available Actions -->
                <v-card v-if="!hasActions" class="mt-4 elevation-2">
                    <v-card-text class="pa-4">
                        <div class="state-banner state-banner--info mb-4">
                            <v-icon color="info" size="32" class="mr-3">info</v-icon>
                            <div class="state-banner__text">
                                <div class="state-banner__title">{{$t('no_acciones_disponibles')}}</div>
                            </div>
                        </div>
                        <v-btn color="primary" large block @click="$router.go(-1)" class="font-weight-bold">
                            <v-icon left>arrow_back</v-icon>
                            {{$t('volver')}}
                        </v-btn>
                    </v-card-text>
                </v-card>

                <!-- Action Buttons -->
                <v-card v-else class="mt-4 elevation-2">
                    <v-card-text class="pa-4">
                        <v-layout row wrap justify-center>
                            <v-flex v-if="acceso.acciones_disponibles.puede_autorizar" xs12 sm6 md4 class="pa-2">
                                <v-btn
                                    color="success"
                                    large
                                    block
                                    :loading="authorizing"
                                    :disabled="rejecting"
                                    @click="authorizeAccess()"
                                    class="font-weight-bold"
                                >
                                    <v-icon left>check_circle</v-icon>
                                    {{$t('autorizar_acceso')}}
                                </v-btn>
                            </v-flex>

                            <v-flex v-if="acceso.acciones_disponibles.puede_rechazar" xs12 sm6 md4 class="pa-2">
                                <v-btn
                                    color="error"
                                    large
                                    block
                                    :loading="rejecting"
                                    :disabled="authorizing"
                                    @click="rejectAccess()"
                                    class="font-weight-bold"
                                >
                                    <v-icon left>cancel</v-icon>
                                    {{$t('rechazar')}}
                                </v-btn>
                            </v-flex>

                            <v-flex xs12 sm6 md4 class="pa-2">
                                <v-btn
                                    color="grey"
                                    dark
                                    large
                                    block
                                    :disabled="authorizing || rejecting"
                                    @click="$router.go(-1)"
                                    class="font-weight-bold"
                                >
                                    <v-icon left>arrow_back</v-icon>
                                    {{$t('volver')}}
                                </v-btn>
                            </v-flex>
                        </v-layout>
                    </v-card-text>
                </v-card>
            </div>
        </v-container>
    </div>
</template>

<script>
    import '@/plugins/axios'
    import store from '@/store'
    import {mapState} from 'vuex'

    export default {
        name: "authorize_access",
        data: () => ({
            acceso: null,
            loading: true,
            error: null,
            errorDetail: null,
            authorizing: false,
            rejecting: false
        }),
        created: function(){
            this.loadAccessDetails()
        },
        computed: {
            ...mapState(['language']),
            accesoId() {
                return this.$route.params.acceso_id
            },
            invitacionStatusLabel() {
                if (!this.acceso || !this.acceso.invitacion) return ''
                const inv = this.acceso.invitacion
                if (inv.authorized) return this.$t('authorized_access')
                if (inv.declined) return this.$t('deny')
                if (inv.canceled) return this.$t('canceled')
                if (inv.confirmed) return this.$t('confirmed')
                return this.$t('pendiente')
            },
            invitacionStatusColor() {
                if (!this.acceso || !this.acceso.invitacion) return 'default'
                const inv = this.acceso.invitacion
                if (inv.authorized) return 'success'
                if (inv.declined) return 'error'
                if (inv.canceled) return 'error'
                if (inv.confirmed) return 'info'
                return 'warning'
            },
            hasActions() {
                if (!this.acceso || !this.acceso.acciones_disponibles) return false
                return this.acceso.acciones_disponibles.puede_autorizar || this.acceso.acciones_disponibles.puede_rechazar
            }
        },
        methods:{
            notify: function (message, color) {
                this.$store.commit('snackbar_change_message', message)
                this.$store.commit('snackbar_change_color', color)
                this.$store.commit('snackbar_activate_true')
            },
            extractApiMessage: function (error) {
                if (error.response) {
                    const data = error.response.data

                    if (typeof data === 'string' && data.trim() !== '') {
                        return data
                    }

                    if (data && typeof data === 'object') {
                        if (data.detail) return data.detail
                        if (data.error) return data.error
                        if (data.message) return data.message
                        if (data.non_field_errors) {
                            return Array.isArray(data.non_field_errors) ? data.non_field_errors.join(', ') : data.non_field_errors
                        }
                        const keys = Object.keys(data)
                        for (let i = 0; i < keys.length; i++) {
                            const value = data[keys[i]]
                            if (Array.isArray(value)) return value.join(', ')
                            if (typeof value === 'string') return value
                        }
                    }

                    return 'Código de estado HTTP: ' + error.response.status
                } else if (error.request) {
                    return 'No se pudo conectar con el servidor. Verifique su conexión a internet.'
                }
                return error.message || 'Error desconocido'
            },
            loadAccessDetails: function () {
                this.loading = true
                this.error = null
                this.errorDetail = null
                this.acceso = null

                const config = {
                    method: 'get',
                    url: 'https://empresa-service.intrare.app/empresas/invitation/detail/' + this.accesoId + '/',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': this.language,
                        'authorization': 'Token ' + store.getters.getToken
                    }
                };

                axios(config)
                    .then(response => {
                        this.acceso = response.data
                        this.loading = false
                    })
                    .catch(error => {
                        this.loading = false
                        this.acceso = null

                        const status = error.response ? error.response.status : null
                        const apiMessage = this.extractApiMessage(error)

                        if (status === 404) {
                            this.error = this.$t('acceso_no_encontrado')
                        } else if (status === 403) {
                            this.error = this.$t('no_autorizado')
                        } else if (status === 401) {
                            this.error = 'Sesión no válida o expirada'
                        } else if (status >= 500) {
                            this.error = 'Error en el servidor'
                        } else {
                            this.error = this.$t('error_cargar_acceso')
                        }
                        this.errorDetail = apiMessage

                        this.notify(this.error + ': ' + apiMessage, 'error')
                        console.error('[authorize_access] Error al cargar acceso ' + this.accesoId + ':', error)
                    })
            },
            authorizeAccess: function () {
                this.authorizing = true

                const config = {
                    method: 'get',
                    url: 'https://empresa-service.intrare.app/AuthorizedAccessCode/' + this.accesoId + '/',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': this.language,
                        'authorization': 'Token ' + store.getters.getToken
                    }
                };

                axios(config)
                    .then(response => {
                        this.authorizing = false

                        const guestName = (this.acceso && this.acceso.invitado && this.acceso.invitado.nombre_completo) ? this.acceso.invitado.nombre_completo : ''
                        const apiMessage = (response.data && (response.data.mensaje || response.data.message)) || this.$t('acceso_autorizado')
                        const message = apiMessage + (guestName ? ' - ' + guestName : '')

                        this.notify(message, 'success')

                        setTimeout(() => {
                            this.$router.go(-1)
                        }, 2000)
                    })
                    .catch(error => {
                        this.authorizing = false
                        const apiMessage = this.extractApiMessage(error)
                        this.notify(this.$t('error_autorizar_acceso') + ': ' + apiMessage, 'error')
                        console.error('[authorize_access] Error al autorizar acceso ' + this.accesoId + ':', error)
                    })
            },
            rejectAccess: function () {
                const qrCode = (this.acceso && this.acceso.invitacion && this.acceso.invitacion.qr_code) ? this.acceso.invitacion.qr_code : null

                if (!qrCode) {
                    this.notify('Código QR no disponible para rechazar esta invitación', 'error')
                    return
                }

                this.rejecting = true

                const config = {
                    method: 'put',
                    url: 'https://empresa-service.intrare.app/declineInvitation/' + qrCode + '/',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': this.language,
                        'authorization': 'Token ' + store.getters.getToken
                    }
                };

                axios(config)
                    .then(response => {
                        this.rejecting = false

                        const guestName = (this.acceso && this.acceso.invitado && this.acceso.invitado.nombre_completo) ? this.acceso.invitado.nombre_completo : ''
                        const apiMessage = (response.data && (response.data.mensaje || response.data.message)) || this.$t('invitacion_rechazada')
                        const message = apiMessage + (guestName ? ' - ' + guestName : '')

                        this.notify(message, 'warning')

                        setTimeout(() => {
                            this.$router.go(-1)
                        }, 2000)
                    })
                    .catch(error => {
                        this.rejecting = false
                        const apiMessage = this.extractApiMessage(error)
                        this.notify(this.$t('error_rechazar_acceso') + ': ' + apiMessage, 'error')
                        console.error('[authorize_access] Error al rechazar acceso ' + this.accesoId + ':', error)
                    })
            }
        }
    }
</script>

<style scoped>
.authorize-access {
    padding: 8px;
}

@media (min-width: 600px) {
    .authorize-access {
        padding: 20px;
    }
}

.rounded-lg {
    border-radius: 8px !important;
}

.info-field__label {
    margin-bottom: 4px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: rgba(0, 0, 0, 0.54);
}

.info-field__value {
    margin-bottom: 0;
    font-size: 15px;
    font-weight: 600;
    color: #002b48;
    word-break: break-word;
}

.info-field__value--break {
    word-break: break-all;
}

.state-banner {
    display: flex;
    align-items: flex-start;
    padding: 16px;
    border-radius: 6px;
    background-color: #f5f5f5;
    border-left: 4px solid #757575;
}

.state-banner--error {
    background-color: #ffebee;
    border-left-color: #d32f2f;
}

.state-banner--success {
    background-color: #e8f5e9;
    border-left-color: #388e3c;
}

.state-banner--info {
    background-color: #e3f2fd;
    border-left-color: #1976d2;
}

.state-banner__text {
    flex: 1;
    min-width: 0;
}

.state-banner__title {
    font-weight: 600;
    font-size: 15px;
    color: rgba(0, 0, 0, 0.87);
    word-break: break-word;
}

.state-banner__detail {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
    margin-top: 4px;
    word-break: break-word;
}

@media (max-width: 599px) {
    .state-banner {
        flex-wrap: wrap;
    }
}
</style>
